import { useMemo, useState } from "react";

import { Link } from "react-router-dom";

import dayjs from "dayjs";
import {
  AlertTriangle,
  Calculator,
  Loader2,
  Lock,
  RefreshCw,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { SearchableSelect } from "~/components/ui/searchable-select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useCostingClosePeriod,
  useCostingMovementReport,
  useCostingSyncAll,
  useEnsureCostingPeriod,
} from "~/hooks/costing";
import { useToast } from "~/hooks/use-toast";
import type { CostingMovementReportRow } from "~/types/costing";

import {
  PeriodSelector,
  fmtCLP,
  fmtQty,
  useCanEditCosting,
  useCosteoPeriod,
  useIsCostingAdmin,
} from "./components/CosteoShared";

const QUICK_LINKS: { to: string; label: string }[] = [
  { to: "/costeo/inventario-inicial", label: "Inventario Final" },
  { to: "/costeo/compras", label: "Compras" },
  { to: "/costeo/movimiento-mp", label: "Mov. Materia Prima" },
  { to: "/costeo/movimiento-pt", label: "Mov. Prod. Terminado" },
  { to: "/costeo/conciliacion", label: "Conciliación" },
];

type StatementLine = {
  label: string;
  qty: number;
  unit: number;
  total: number;
  variant: "base" | "op" | "subtotal" | "result";
};

const perUnit = (value: number, qty: number) =>
  Math.abs(qty) > 0.0001 ? value / qty : 0;

/** Build the vertical movement statement for one finished good (matches the
 * accounting "Movimiento de Producto Terminado" report). */
function buildStatement(r: CostingMovementReportRow): StatementLine[] {
  const wac = Number(r.wac_clp);
  const lines: StatementLine[] = [
    {
      label: "Inventario Inicial",
      qty: r.opening_qty,
      unit: perUnit(r.opening_value, r.opening_qty),
      total: r.opening_value,
      variant: "base",
    },
    {
      label: "(+) Producción",
      qty: r.production_qty,
      unit: perUnit(r.production_value, r.production_qty),
      total: r.production_value,
      variant: "op",
    },
  ];
  if (r.purchases_qty > 0)
    lines.push({
      label: "(+) Compras",
      qty: r.purchases_qty,
      unit: perUnit(r.purchases_value, r.purchases_qty),
      total: r.purchases_value,
      variant: "op",
    });
  if (r.adjustments_in_qty > 0)
    lines.push({
      label: "(+) Ajustes (entrada)",
      qty: r.adjustments_in_qty,
      unit: perUnit(r.adjustments_in_value, r.adjustments_in_qty),
      total: r.adjustments_in_value,
      variant: "op",
    });
  lines.push({
    label: "= Disponibilidad",
    qty: r.availability_qty,
    unit: wac,
    total: r.availability_value,
    variant: "subtotal",
  });
  lines.push({
    label: "(−) Ventas",
    qty: r.sales_qty,
    unit: wac,
    total: r.sales_value,
    variant: "op",
  });
  lines.push({
    label: "(+) Devoluciones",
    qty: r.returns_qty,
    unit: wac,
    total: r.returns_value,
    variant: "op",
  });
  if (r.waste_qty > 0)
    lines.push({
      label: "(−) Merma",
      qty: r.waste_qty,
      unit: wac,
      total: r.waste_value,
      variant: "op",
    });
  if (r.adjustments_out_qty > 0)
    lines.push({
      label: "(−) Ajustes (salida)",
      qty: r.adjustments_out_qty,
      unit: wac,
      total: r.adjustments_out_value,
      variant: "op",
    });
  lines.push({
    label: "= Inventario Final Teórico",
    qty: r.closing_qty,
    unit: wac,
    total: r.closing_value,
    variant: "subtotal",
  });
  lines.push({
    label: "⇒ Costo de Ventas",
    qty: r.cost_of_sales_qty,
    unit: wac,
    total: r.cost_of_sales_value,
    variant: "result",
  });
  return lines;
}

export function CosteoDashboardPage() {
  const { toast } = useToast();
  const canEdit = useCanEditCosting();
  const isAdmin = useIsCostingAdmin();

  const { periods, period, periodRow, setPeriod, isLoading } =
    useCosteoPeriod();
  const { data: report = [], isLoading: reportLoading } =
    useCostingMovementReport(period);

  const ensurePeriod = useEnsureCostingPeriod();
  const syncAll = useCostingSyncAll();
  const closePeriod = useCostingClosePeriod();

  const [confirmClose, setConfirmClose] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );

  const currentPeriodLabel = dayjs().format("YYYY-MM");
  const isOpen = periodRow?.status === "open";

  // Finished goods present in the period, by descending availability value.
  const products = useMemo(
    () =>
      report
        .filter((r) => r.item_kind === "finished_good")
        .sort(
          (a, b) => Number(b.availability_value) - Number(a.availability_value)
        ),
    [report]
  );

  const selected = useMemo(
    () =>
      products.find((p) => p.product_id === selectedProductId) ??
      products[0] ??
      null,
    [products, selectedProductId]
  );

  const periodTotals = useMemo(() => {
    const fg = report.filter((r) => r.item_kind === "finished_good");
    const mp = report.filter((r) => r.item_kind !== "finished_good");
    return {
      costOfSales: fg.reduce((s, r) => s + Number(r.cost_of_sales_value), 0),
      closingFg: fg.reduce((s, r) => s + Number(r.closing_value), 0),
      closingMp: mp.reduce((s, r) => s + Number(r.closing_value), 0),
      negativeItems: report.filter((r) => Number(r.closing_qty) < -0.5).length,
    };
  }, [report]);

  const statement = selected ? buildStatement(selected) : [];

  const handleCreatePeriod = async () => {
    try {
      await ensurePeriod.mutateAsync(currentPeriodLabel);
      setPeriod(currentPeriodLabel);
      toast({ title: "Período creado", description: currentPeriodLabel });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    }
  };

  const handleSyncAll = async () => {
    try {
      const result = await syncAll.mutateAsync(period);
      toast({
        title: "Sincronización completa",
        description: `Compras: ${result.purchases.synced} · Consumos: ${result.consumption.synced} · Producción: ${result.production.synced} · Ventas: ${result.sales.sales_synced} · Devoluciones: ${result.sales.returns_synced} · Ajustes: ${(result.adjustments.ingredients ?? 0) + (result.adjustments.products ?? 0)}`,
      });
    } catch (error) {
      toast({
        title: "Error al sincronizar",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    }
  };

  const handleClose = async () => {
    try {
      const result = await closePeriod.mutateAsync(period);
      toast({
        title: `Período ${result.closed} cerrado`,
        description: `Se sembraron ${result.opening_rows_seeded} saldos iniciales en ${result.next_period}`,
      });
      setConfirmClose(false);
    } catch (error) {
      toast({
        title: "Error al cerrar período",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    }
  };

  return (
    <PageContainer>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Calculator className="h-6 w-6" />
            Costeo de Inventario
          </h1>
          <p className="text-muted-foreground">
            Costo promedio ponderado diario, costo de ventas y margen bruto
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodSelector
            periods={periods}
            value={period}
            onChange={setPeriod}
          />
          {periodRow && (
            <Badge variant={isOpen ? "default" : "secondary"}>
              {isOpen ? "Abierto" : "Cerrado"}
            </Badge>
          )}
          {canEdit && (
            <>
              <Button
                variant="outline"
                className="h-9"
                onClick={handleSyncAll}
                disabled={!isOpen || syncAll.isPending}
              >
                {syncAll.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Sincronizar y Recalcular
              </Button>
              {isAdmin && (
                <Button
                  variant="destructive"
                  className="h-9"
                  onClick={() => setConfirmClose(true)}
                  disabled={!isOpen}
                >
                  <Lock className="mr-2 h-4 w-4" />
                  Cerrar Período
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Period totals — compact inline strip (not panels) */}
      {period && (
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 border-y py-3 text-sm">
          <span>
            <span className="text-muted-foreground">Costo de Ventas: </span>
            <span className="font-semibold">
              {fmtCLP(periodTotals.costOfSales)}
            </span>
          </span>
          <span>
            <span className="text-muted-foreground">
              Inv. Final PT (teórico):{" "}
            </span>
            <span className="font-semibold">
              {fmtCLP(periodTotals.closingFg)}
            </span>
          </span>
          <span>
            <span className="text-muted-foreground">
              Inv. Final MP (teórico):{" "}
            </span>
            <span className="font-semibold">
              {fmtCLP(periodTotals.closingMp)}
            </span>
          </span>
        </div>
      )}

      {!isLoading && periods.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-lg border py-10 text-center">
          <p className="text-muted-foreground">
            Aún no hay períodos de costeo. Cree el período actual para comenzar.
          </p>
          {canEdit && (
            <Button
              onClick={handleCreatePeriod}
              disabled={ensurePeriod.isPending}
            >
              Crear período {currentPeriodLabel}
            </Button>
          )}
        </div>
      )}

      {periodTotals.negativeItems > 0 && (
        <Link
          to="/costeo/conciliacion"
          className="flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900 transition-colors hover:bg-amber-100"
        >
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span>
            <span className="font-semibold">{periodTotals.negativeItems}</span>{" "}
            ítems con inventario teórico negativo — probablemente falta cargar
            el inventario inicial o compras. Revisar en Conciliación.
          </span>
        </Link>
      )}

      {/* Movimiento de Producto Terminado — vertical statement per product */}
      {period && (
        <div className="space-y-3 rounded-lg border bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">
                Movimiento de Producto Terminado
              </h2>
              <p className="text-sm text-muted-foreground">
                {selected
                  ? `${selected.item_name} · ${selected.item_code}`
                  : "Sin productos en el período"}
              </p>
            </div>
            <div className="w-80 max-w-full">
              <SearchableSelect
                value={selected?.product_id ?? ""}
                onValueChange={setSelectedProductId}
                options={products.map((p) => ({
                  value: p.product_id ?? "",
                  label: `${p.item_code ?? "—"} · ${p.item_name ?? ""}`,
                }))}
                placeholder="Seleccionar producto…"
                searchPlaceholder="Buscar por código o nombre…"
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%]">Movimiento</TableHead>
                <TableHead className="text-right">Cantidad</TableHead>
                <TableHead className="text-right">Costo Unitario</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reportLoading ? (
                Array.from({ length: 7 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={4}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : !selected ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="py-8 text-center text-muted-foreground"
                  >
                    Sin productos terminados con movimiento en {period}
                  </TableCell>
                </TableRow>
              ) : (
                statement.map((line) => {
                  const isSubtotal = line.variant === "subtotal";
                  const isResult = line.variant === "result";
                  const negative = line.variant === "subtotal" && line.qty < 0;
                  return (
                    <TableRow
                      key={line.label}
                      className={
                        isResult
                          ? "bg-emerald-50 font-bold"
                          : isSubtotal
                            ? "border-t-2 font-semibold"
                            : ""
                      }
                    >
                      <TableCell>{line.label}</TableCell>
                      <TableCell
                        className={`text-right ${negative ? "text-destructive" : ""}`}
                      >
                        {fmtQty(line.qty)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmtCLP(line.unit)}
                      </TableCell>
                      <TableCell
                        className={`text-right ${negative ? "text-destructive" : ""}`}
                      >
                        {fmtCLP(line.total)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Plain-text navigation (no panels) */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
        <span>Ir a:</span>
        {QUICK_LINKS.map((l) => (
          <Link key={l.to} to={l.to} className="text-primary hover:underline">
            {l.label}
          </Link>
        ))}
      </div>

      {/* Close-period confirmation */}
      <Dialog open={confirmClose} onOpenChange={setConfirmClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cerrar período {period}</DialogTitle>
            <DialogDescription>
              Se recalculará el costo promedio final, se congelarán los costos y
              el inventario final se sembrará como inventario inicial del
              período siguiente.{" "}
              <span className="font-medium text-destructive">
                Esta acción no se puede deshacer.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmClose(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleClose}
              disabled={closePeriod.isPending}
            >
              {closePeriod.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Cerrar Período
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
